import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  CrossClientQueryEvaluation,
  CrossClientQueryEvaluationStatus,
  CrossClientQueryEvaluationStatuses,
  CrossClientQueryEvaluationType,
  CrossClientQueryEvaluationTypes,
  CrossClientQueryExecution,
  CrossClientQueryResult,
  CrossClientQueryResultBucketCount,
  CrossClientQueryResultMatches,
} from '../../models/CrossClient'
import { User } from '../../models/User'
import { bulkIsRunning, executionIsActive } from './utils'

const EMPTY_EVALUATIONS: CrossClientQueryEvaluation[] = []
const EMPTY_BUCKETS: CrossClientQueryResultBucketCount[] = []

const TERMINAL_EVALUATION_STATUSES: CrossClientQueryEvaluationStatus[] = [
  CrossClientQueryEvaluationStatuses.COMPLETED,
  CrossClientQueryEvaluationStatuses.CANCELED,
  CrossClientQueryEvaluationStatuses.ERRORED,
]

export const isTerminalEvaluationStatus = (status: CrossClientQueryEvaluationStatus): boolean => TERMINAL_EVALUATION_STATUSES.includes(status)

interface CrossClientResultsState {
  execution: CrossClientQueryExecution | null
  executionLoading: boolean
  resultBuckets: CrossClientQueryResultBucketCount[]
  // The execution the buckets belong to, so the page can wait for its own counts.
  resultBucketsFor: string | null
  // Bumped whenever the page shifts the counts itself, and again once that change is saved: a read started before then is stale.
  resultBucketsRevision: number
  // Moves the page has counted that aren't saved yet; no read can know about them.
  resultBucketsHolds: number
  activeEvaluations: CrossClientQueryEvaluation[]
  clients: EntityState<User, string>
  fetchedClientIds: Record<string, true>
  // Clients whose lookup finished, found or not, so rows stop waiting for them.
  lookedUpClientIds: Record<string, true>
  teammates: User[]
  pollingEnabled: boolean
  // The execution whose first counts the poller gave up on; its page offers to try again.
  countsUnavailableFor: string | null
  // Bumped whenever results may have changed; every list refetches on it.
  resultsRevision: number
}

const crossClientClientsAdapter = createEntityAdapter<User>()

const initialState: CrossClientResultsState = {
  execution: null,
  executionLoading: true,
  resultBuckets: [],
  resultBucketsFor: null,
  resultBucketsRevision: 0,
  resultBucketsHolds: 0,
  activeEvaluations: [],
  clients: crossClientClientsAdapter.getInitialState(),
  fetchedClientIds: {},
  lookedUpClientIds: {},
  teammates: [],
  pollingEnabled: true,
  countsUnavailableFor: null,
  resultsRevision: 0,
}

export const crossClientResultsSlice = createSlice({
  name: 'crossClientResults',
  initialState,
  reducers: {
    setCrossClientResultsExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.executionLoading = action.payload
    },
    startCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientExecutionPolling: (state) => {
      state.pollingEnabled = false
    },
    giveUpCrossClientCounts: (state, action: PayloadAction<string>) => {
      state.pollingEnabled = false
      state.countsUnavailableFor = action.payload
    },
    retryCrossClientCounts: (state) => {
      state.countsUnavailableFor = null
      state.pollingEnabled = true
    },
    // Always dispatched right after their execution, so state.execution is the one they were read for. A read passes the
    // revision it started at, and is dropped when the page has shifted the counts since.
    setCrossClientResultBuckets: {
      reducer: (state, action: PayloadAction<{ buckets: CrossClientQueryResultBucketCount[]; readRevision?: number }>) => {
        const { buckets, readRevision } = action.payload
        if (readRevision !== undefined && (readRevision !== state.resultBucketsRevision || state.resultBucketsHolds > 0)) {
          return
        }
        state.resultBuckets = buckets
        state.resultBucketsFor = state.execution?.id ?? null
      },
      prepare: (buckets: CrossClientQueryResultBucketCount[], readRevision?: number) => ({ payload: { buckets, readRevision } }),
    },
    // The page counts a move ahead of saving it.
    holdCrossClientResultBuckets: (state, action: PayloadAction<CrossClientQueryResultBucketCount[]>) => {
      state.resultBuckets = action.payload
      state.resultBucketsRevision += 1
      state.resultBucketsHolds += 1
    },
    // The move is saved, or undone with the counts it gave back.
    releaseCrossClientResultBuckets: (state, action: PayloadAction<CrossClientQueryResultBucketCount[] | undefined>) => {
      if (action.payload !== undefined) {
        state.resultBuckets = action.payload
      }
      state.resultBucketsRevision += 1
      state.resultBucketsHolds = Math.max(state.resultBucketsHolds - 1, 0)
    },
    // The poller lists only live evaluations: one that drops out has finished, so it stays COMPLETED until none is live.
    mergeCrossClientActiveEvaluations: (state, action: PayloadAction<CrossClientQueryEvaluation[]>) => {
      const active = action.payload
      if (active.length === 0) {
        state.activeEvaluations = []
        return
      }
      const activeIds = new Set(active.map((evaluation) => evaluation.id))
      const retained = state.activeEvaluations
        .filter((evaluation) => !activeIds.has(evaluation.id))
        .map((evaluation) =>
          isTerminalEvaluationStatus(evaluation.status) ? evaluation : { ...evaluation, status: CrossClientQueryEvaluationStatuses.COMPLETED },
        )
      state.activeEvaluations = [...active, ...retained]
    },
    addCrossClientActiveEvaluation: (state, action: PayloadAction<CrossClientQueryEvaluation>) => {
      if (!state.activeEvaluations.some((evaluation) => evaluation.id === action.payload.id)) {
        state.activeEvaluations.push(action.payload)
      }
    },
    setCrossClientResultsExecution: (state, action: PayloadAction<CrossClientQueryExecution | null>) => {
      state.execution = action.payload
    },
    addCrossClientClients: (state, action: PayloadAction<User[]>) => {
      crossClientClientsAdapter.addMany(state.clients, action.payload)
    },
    addCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      state.fetchedClientIds = action.payload.reduce((acc, id) => ({ ...acc, [id]: true }), state.fetchedClientIds)
    },
    removeCrossClientFetchedClientIds: (state, action: PayloadAction<string[]>) => {
      state.fetchedClientIds = Object.fromEntries(Object.entries(state.fetchedClientIds).filter(([id]) => !action.payload.includes(id))) as Record<string, true>
    },
    addCrossClientLookedUpClientIds: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((id) => {
        state.lookedUpClientIds[id] = true
      })
    },
    setCrossClientTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    bumpCrossClientResultsRevision: (state) => {
      state.resultsRevision += 1
    },
    resetCrossClientResults: () => initialState,
  },
})

export const {
  setCrossClientResultsExecutionLoading,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
  giveUpCrossClientCounts,
  retryCrossClientCounts,
  setCrossClientResultBuckets,
  holdCrossClientResultBuckets,
  releaseCrossClientResultBuckets,
  mergeCrossClientActiveEvaluations,
  addCrossClientActiveEvaluation,
  setCrossClientResultsExecution,
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  removeCrossClientFetchedClientIds,
  addCrossClientLookedUpClientIds,
  setCrossClientTeammates,
  bumpCrossClientResultsRevision,
  resetCrossClientResults,
} = crossClientResultsSlice.actions

const crossClientClientSelectors = crossClientClientsAdapter.getSelectors((state: RootState) => state.crossClientResults.clients)

export const selectCrossClientResultsExecution = (state: RootState) => state.crossClientResults.execution

export const selectCrossClientResultsRevision = (state: RootState) => state.crossClientResults.resultsRevision

export const selectCrossClientResultExecutionLoading = (state: RootState) => state.crossClientResults.executionLoading

export const selectCrossClientResultBuckets = (state: RootState) => state.crossClientResults.resultBuckets ?? EMPTY_BUCKETS

export const selectCrossClientResultBucketsRevision = (state: RootState) => state.crossClientResults.resultBucketsRevision

export const selectCrossClientResultBucketsFor = (state: RootState) => state.crossClientResults.resultBucketsFor

export const selectCrossClientActiveEvaluations = (state: RootState) => state.crossClientResults.activeEvaluations ?? EMPTY_EVALUATIONS

export const selectCrossClientTeammates = createSelector([(state: RootState) => state.crossClientResults.teammates], (teammates) =>
  teammates.reduce(
    (teammateMap, teammate) => {
      teammateMap[teammate.id] = teammate
      return teammateMap
    },
    {} as Record<string, User>,
  ),
)

export const selectCrossClientClients = createSelector([crossClientClientSelectors.selectAll], (clients) =>
  clients.reduce(
    (clientMap, client) => {
      clientMap[client.id] = client
      return clientMap
    },
    {} as Record<string, User>,
  ),
)

export const selectCrossClientFetchedClientIds = (state: RootState) => state.crossClientResults.fetchedClientIds
export const selectCrossClientLookedUpClientIds = (state: RootState) => state.crossClientResults.lookedUpClientIds

export const selectCrossClientResultsPollingEnabled = (state: RootState) => state.crossClientResults.pollingEnabled
export const selectCrossClientCountsUnavailableFor = (state: RootState) => state.crossClientResults.countsUnavailableFor

export const selectShouldPollCrossClientExecutionResults = (state: RootState) => executionIsActive(state.crossClientResults.execution)

// Only AI is startable: the merged step runs local.
export const nextStepKindForResult = (result: CrossClientQueryResult): CrossClientQueryEvaluationType | null =>
  result.match === CrossClientQueryResultMatches.POTENTIAL || result.match === null || result.match === undefined ? CrossClientQueryEvaluationTypes.AI : null

// The list keeps finished evaluations, relabelled COMPLETED.
export const selectCrossClientResolversInFlight = createSelector(
  [selectCrossClientResultsExecution, selectCrossClientActiveEvaluations],
  (execution, activeEvaluations): boolean =>
    (!!execution && bulkIsRunning(execution)) ||
    activeEvaluations.some(
      (evaluation) => evaluation.type === CrossClientQueryEvaluationTypes.LOCAL_RESOLVER && !isTerminalEvaluationStatus(evaluation.status),
    ),
)

export default crossClientResultsSlice.reducer
